import React, { useState, useEffect } from 'react';
import { applyForJob } from '../services/jobsAPI';
import { User } from '../../../App';

interface ApplyJobModalProps {
  jobId: number;
  jobTitle: string;
  onClose: () => void;
  user: User;
}

const InputField: React.FC<{
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
}> = ({ label, name, value, onChange, placeholder, type = 'text', required = true, disabled = false }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && !disabled && <span className="text-red-500 ml-1">*</span>}
    </label>
    <input
      type={type}
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      className="w-full px-3 py-2 border border-gray-400 bg-gray-50 text-gray-900 placeholder-gray-500 rounded-md shadow-sm focus:outline-none focus:bg-white focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/50 transition-colors disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
    />
  </div>
);

const ApplyJobModal: React.FC<ApplyJobModalProps> = ({ jobId, jobTitle, onClose, user }) => {
  const [formData, setFormData] = useState({
    caregiver_id: '',
    name: '',
    mobile: '',
    from: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const isCarePro = user && user.id !== 'Guest';

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    // Pre-fill form if user is not a guest
    if (isCarePro) {
      const location = [user.city, user.country].filter(Boolean).join(', ');
      setFormData(prev => ({
        ...prev,
        caregiver_id: user.id,
        name: `${user.firstName} ${user.lastName}`.trim(),
        mobile: user.mobile || '',
        from: location,
      }));
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [user, isCarePro]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isCarePro && (!formData.name || !formData.mobile || !formData.from)) {
      setError('Please fill out all mandatory fields.');
      return;
    }
    setError(null);
    setIsSubmitting(true);

    try {
      const payload: {
        caregiver_id: string | null;
        name: string;
        mobile: string;
        from: string;
      } = {
        name: formData.name,
        mobile: formData.mobile,
        from: formData.from,
        caregiver_id: formData.caregiver_id,
      };
      
      if (isCarePro) {
        payload.name = '';
        payload.mobile = '';
        payload.from = '';
      } else {
        payload.caregiver_id = null;
      }
      
      await applyForJob(jobId, payload);
      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred during submission.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const SuccessView = () => (
    <div className="p-8 text-center">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-2xl font-bold text-gray-800 mt-4">Application Sent!</h3>
      <p className="text-gray-600 mt-2">Your application for "{jobTitle}" has been successfully submitted. We will get back to you soon.</p>
      <button 
        onClick={onClose}
        className="mt-6 w-full sm:w-auto inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand-purple text-base font-medium text-white hover:bg-brand-purple/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-purple"
      >
        Close
      </button>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="apply-modal-title"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg m-4 transform transition-all animate-fade-in-up"
        onClick={e => e.stopPropagation()}
      >
        {isSuccess ? <SuccessView /> : (
          <>
            <div className="p-5 border-b flex justify-between items-center">
              <h3 id="apply-modal-title" className="text-lg font-bold text-gray-800">
                Apply for: <span className="text-brand-purple">{jobTitle}</span>
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close modal">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} noValidate>
              <div className="p-6 space-y-4">
                <InputField label="Caregiver ID" name="caregiver_id" value={formData.caregiver_id} onChange={handleChange} placeholder="Not applicable" disabled={true} required={false} />
                <InputField label="Full Name" name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" disabled={isCarePro} />
                <InputField label="Mobile Number" name="mobile" type="tel" value={formData.mobile} onChange={handleChange} placeholder="e.g., 0123456789" disabled={isCarePro} />
                <InputField label="Where are you from?" name="from" value={formData.from} onChange={handleChange} placeholder="e.g., Kuala Lumpur, Malaysia" disabled={isCarePro} />
                {error && (
                  <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm" role="alert">
                    {error}
                  </div>
                )}
              </div>
              <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-purple border border-transparent rounded-md shadow-sm hover:bg-brand-purple/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-purple disabled:bg-brand-light-purple disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ApplyJobModal;